import {
  LockOutlined,
  MailOutlined,
  MessageOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { Button, Card, Input, Result, Spin, Typography, message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CandidateMessage } from '../../../private/modules/job-application/models/candidate-message.model';
import { CandidateMessageService } from '../../../private/modules/job-application/services/candidate-message.service';
import './CandidateConversationPage.css';

const formatMessageTime = (value: string): string =>
  new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const CandidateConversationPage: React.FC = () => {
  const { token = '' } = useParams();
  const [phase, setPhase] = useState<'loading' | 'ready' | 'gone'>('loading');
  const [candidateName, setCandidateName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [messages, setMessages] = useState<CandidateMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void (async () => {
      try {
        const thread = await CandidateMessageService.publicView(token);
        setCandidateName(thread.candidateName);
        setJobTitle(thread.jobTitle);
        setCompanyName(thread.companyName);
        setExpiresAt(thread.expiresAt);
        setMessages(thread.messages || []);
        setPhase('ready');
      } catch {
        setPhase('gone');
      }
    })();
  }, [token]);

  useEffect(() => {
    if (phase === 'ready') endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, phase]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const sent = await CandidateMessageService.reply(token, body);
      setMessages((current) => [...current, sent]);
      setDraft('');
      message.success('Reply sent.');
    } catch (error) {
      if (error instanceof Error && error.message === 'GONE') {
        setPhase('gone');
      } else {
        message.error('Could not send your reply.');
      }
    } finally {
      setSending(false);
    }
  };

  if (phase === 'loading') {
    return (
      <main className="candidate-conversation-public" role="status">
        <Spin size="large" tip="Loading secure conversation…" />
      </main>
    );
  }

  if (phase === 'gone') {
    return (
      <main className="candidate-conversation-public">
        <Result
          status="warning"
          title="This conversation link is no longer available"
          subTitle="Ask the recruiting team to send you a new message link."
        />
      </main>
    );
  }

  return (
    <main className="candidate-conversation-public">
      <Card className="candidate-conversation-public__card">
        <header>
          <span aria-hidden="true">
            <MessageOutlined />
          </span>
          <div>
            <small>{companyName}</small>
            <Typography.Title level={2}>
              Conversation with recruiting
            </Typography.Title>
            <Typography.Paragraph>
              {candidateName} · {jobTitle}
            </Typography.Paragraph>
          </div>
        </header>

        <div className="candidate-conversation-public__trust">
          <LockOutlined />
          <span>
            <strong>Private application conversation</strong>
            Messages here do not change your application status.
          </span>
        </div>

        <section
          className="candidate-conversation-public__messages"
          aria-live="polite"
        >
          {messages.map((item) => (
            <article
              key={item.id}
              className={`candidate-conversation-public__message candidate-conversation-public__message--${item.direction.toLowerCase()}`}
            >
              <div>
                <strong>{item.senderName}</strong>
                <time dateTime={item.createdAt}>
                  {formatMessageTime(item.createdAt)}
                </time>
              </div>
              <p>{item.body}</p>
              {item.direction === 'RECRUITER' && item.emailQueued ? (
                <small>
                  <MailOutlined /> Delivered by email
                </small>
              ) : null}
            </article>
          ))}
          <div ref={endRef} />
        </section>

        <section className="candidate-conversation-public__composer">
          <Input.TextArea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onPressEnter={(event) => {
              if (event.shiftKey) return;
              event.preventDefault();
              void send();
            }}
            autoSize={{ minRows: 3, maxRows: 7 }}
            maxLength={5000}
            showCount
            disabled={sending}
            placeholder="Write a reply…"
            aria-label="Write a reply"
          />
          <div>
            <span>Enter to send · Shift+Enter for a new line</span>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              disabled={!draft.trim()}
              onClick={() => void send()}
            >
              Send
            </Button>
          </div>
        </section>

        <footer>
          Secure conversation · Link expires{' '}
          {expiresAt ? new Date(expiresAt).toLocaleDateString() : ''}
        </footer>
      </Card>
    </main>
  );
};

export default CandidateConversationPage;
